import AnimatedSection from "@/components/AnimatedSection";

const TIMELINE = [
  { year: "1976", title: "The Restaurant Opens", text: "Amrut Seafood Restaurant starts serving in Karwar. Fresh fish, traditional recipes, and a kitchen that never cuts corners. The foundation of everything that follows." },
  { year: "1999", title: "Amrut Jewellers", text: "The jewellery store opens — hallmarked gold, certified diamonds, and handmade temple jewellery. A trusted name from day one." },
  { year: "2012", title: "Amrut Inn", text: "A 32-room budget hotel opens for travellers and families who want clean rooms, good food, and fair pricing." },
  { year: "2019", title: "Amrut Delight", text: "Two bakery outlets launch in Karwar — handmade cakes, fresh patisserie, and in-house manufacturing for both retail and wholesale." },
  { year: "2020", title: "Amrut Aura", text: "The premium hotel opens — fully air-conditioned, mountain views, conference facilities, and the best sleep quality in town." },
  { year: "Today", title: "Amrut Group", text: "Five businesses, one family, same values. Featured on History Channel and NDTV Good Times. Recognized among India's top 200 restaurants by Times of India and listed among 75 iconic restaurants in India." },
];

const VALUES = [
  { title: "Keep It Real", text: "No additives in the food, no gimmicks in the service. What you see is what you get — and it's always been that way." },
  { title: "Take Care of People", text: "Our staff has been with us for years. They know the regulars, they remember preferences, and they treat every guest like family." },
  { title: "Earn the Return Visit", text: "We don't chase new customers at the cost of old ones. Most of our business comes from people who've been here before. That's how we measure success." },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* ─── HERO ─── */}
      <section className="pt-28 md:pt-36 pb-12 md:pb-16 px-6 md:px-10 lg:px-16">
        <div className="max-w-[1200px] mx-auto">
          <AnimatedSection><p className="overline-text text-charcoal/40 mb-6">Our Story</p></AnimatedSection>
          <AnimatedSection delay={0.1}><h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-charcoal font-light tracking-tighter leading-none mb-8">The Amrut Story</h1></AnimatedSection>
          <AnimatedSection delay={0.2}><div className="gold-divider mb-10" /></AnimatedSection>
          <AnimatedSection delay={0.3}>
            <p className="text-base md:text-lg text-charcoal/60 leading-relaxed max-w-2xl">
              It started with a restaurant in 1976. A small place in Karwar that served fresh
              fish cooked the local way — no shortcuts, no additives. People kept coming back.
              Over the years, that same approach built a hotel, then another, then a bakery,
              then a jewellery store. Today it's five businesses, but the thinking hasn't changed.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* ─── NARRATIVE ─── */}
      <section className="py-16 md:py-24 px-6 md:px-10 lg:px-16">
        <div className="max-w-[900px] mx-auto">
          <AnimatedSection>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-charcoal font-light tracking-tight leading-tight mb-10">Same family,<br />same standards</h2>
          </AnimatedSection>
          <div className="space-y-8">
            <AnimatedSection delay={0.1}>
              <p className="text-base md:text-lg text-charcoal/60 leading-relaxed">
                Every Amrut business is owned and operated by the same family. The restaurant,
                the hotels, the bakery, the jewellery store — they're all connected. Not just by name,
                but by how things are done. Fresh ingredients. Clean spaces. Staff that stays for years.
                Guests that come back for decades.
              </p>
            </AnimatedSection>
            <AnimatedSection delay={0.15}>
              <p className="text-base md:text-lg text-charcoal/60 leading-relaxed">
                The restaurant has been recognized among India's 75 iconic restaurants and featured
                on History Channel and NDTV Good Times. But honestly, the thing we're most proud of
                is the regulars who walk in every week and the guests who've been booking rooms
                with us since the beginning. That kind of trust isn't something you can advertise.
                You have to earn it.
              </p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* ─── TIMELINE ─── */}
      <section data-testid="about-timeline" className="py-16 md:py-24 px-6 md:px-10 lg:px-16 bg-ivory">
        <div className="max-w-[1000px] mx-auto">
          <AnimatedSection><p className="overline-text text-charcoal/50 mb-6">The Journey</p><h2 className="font-serif text-4xl md:text-5xl text-charcoal font-light tracking-tight mb-12">How We Got Here</h2></AnimatedSection>
          {TIMELINE.map((item, i) => (
            <AnimatedSection key={i} delay={i * 0.1}>
              <div className="border-t border-charcoal/15 py-8 md:py-10 grid grid-cols-1 md:grid-cols-[120px_1fr] gap-3 md:gap-12">
                <p className="font-serif text-3xl md:text-4xl text-gold font-light">{item.year}</p>
                <div>
                  <h4 className="font-serif text-xl md:text-2xl text-charcoal font-light tracking-tight mb-3">{item.title}</h4>
                  <p className="text-sm text-charcoal/60 leading-relaxed max-w-lg">{item.text}</p>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* ─── VALUES ─── */}
      <section className="py-16 md:py-24 px-6 md:px-10 lg:px-16">
        <div className="max-w-[1200px] mx-auto">
          <AnimatedSection><p className="overline-text text-charcoal/40 mb-6">What We Believe</p><div className="gold-divider mb-12" /></AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {VALUES.map((v, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <h4 className="font-serif text-2xl md:text-3xl text-charcoal font-light tracking-tight mb-5">{v.title}</h4>
                <p className="text-sm text-charcoal/55 leading-relaxed">{v.text}</p>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ─── VISION ─── */}
      <section className="py-16 md:py-24 px-6 md:px-10 lg:px-16 bg-ivory text-center">
        <div className="max-w-[800px] mx-auto">
          <AnimatedSection><p className="overline-text text-charcoal/50 mb-8">Looking Ahead</p></AnimatedSection>
          <AnimatedSection delay={0.1}>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-charcoal font-light tracking-tight leading-tight italic">
              "We don't want to be the biggest. We want to be the place people trust —
              for a room, a meal, a cake, or a piece of gold. In Karwar and beyond."
            </h2>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
